import { Paper, styled } from "@mui/material";
import {
    GetFieldLookup,
    GetFieldLookupItem,
    useFormMetadata,
    useFormSave,
    useLookups,
    useSnackbar,
    type ILookup,
} from "@ngt/forms";
import { FC, useCallback, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { FormDefinitions, ProjectPrecinct, ProjectPrecinctApprovals } from "../api/dtos";
import { StyledButton } from "../components/StyledButton";
import CollapsibleAppTable from "../components/tables/CollapsibleAppTable";
import type { AppColumn, AppScalarValue } from "../components/tables/AppTable.types";
import TitleBox from "../components/TitleBox";
import ProjectContext from "../contexts/data/ProjectContext";
import useProjectPrecinctsByProjectId from "../hooks/data/useProjectPrecinctsByProjectId";

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

function setPrecinctField<K extends keyof ProjectPrecinct>(
    row: ProjectPrecinct,
    key: K,
    value: ProjectPrecinct[K],
): ProjectPrecinct {
    const updated = Object.assign(Object.create(Object.getPrototypeOf(row)) as ProjectPrecinct, row);

    updated[key] = value;

    return updated;
}

function precinctApprovalOptions(lookups: ILookup[] | null | undefined) {
    return (GetFieldLookup(lookups, "PrecinctApproval")?.items ?? []).flatMap((item) => {
        if (item.id === null || item.id === undefined || item.id === "") {
            return [];
        }

        const value = typeof item.id === "number" ? item.id : Number(item.id);

        return Number.isFinite(value)
            ? [
                  {
                      value,
                      label: item.value?.toString() ?? String(value),
                  },
              ]
            : [];
    });
}

function precinctApprovalLabel(lookups: ILookup[] | null | undefined, value: AppScalarValue): string {
    if (value === null || value === undefined) {
        return "";
    }

    return GetFieldLookupItem(lookups, "PrecinctApproval", value)?.value?.toString() ?? String(value);
}

export const buildProjectPrecinctColumns = (lookups: ILookup[] | null | undefined): AppColumn<ProjectPrecinct>[] => [
    {
        id: "name",
        label: "Name",
        accessor: (row) => row.name,
        scalarType: "text",
        flex: 1,
        setValue: (row, value) => setPrecinctField(row, "name", typeof value === "string" ? value : undefined),
    },
    {
        id: "sageCode",
        label: "Sage Code",
        accessor: (row) => row.sageCode,
        scalarType: "text",
        flex: 1,
        setValue: (row, value) => setPrecinctField(row, "sageCode", typeof value === "string" ? value : undefined),
    },
    {
        id: "precinctApproval",
        label: "Precinct Approval",
        accessor: (row) => row.precinctApproval,
        scalarType: "lookup",
        flex: 1,
        lookup: {
            options: precinctApprovalOptions(lookups),
            labelFor: (value) => precinctApprovalLabel(lookups, value),
        },
        exportAccessor: ({ value }) => value,
        setValue: (row, value) =>
            setPrecinctField(
                row,
                "precinctApproval",
                typeof value === "number" ? (value as ProjectPrecinctApprovals) : undefined,
            ),
    },
];

function errorMessages(reason: unknown): string[] {
    if (typeof reason !== "object" || reason === null) {
        return ["Unable to save project precinct."];
    }

    const responseStatus = (reason as { responseStatus?: { errors?: readonly { message?: string }[] } }).responseStatus;
    const messages = responseStatus?.errors
        ?.map((error) => error.message)
        .filter((message): message is string => Boolean(message));

    return messages?.length ? messages : ["Unable to save project precinct."];
}

export const BulkEditProjectPrecincts: FC = () => {
    const snackbar = useSnackbar();
    const projectPrecinctMetadata = useFormMetadata(FormDefinitions.ProjectPrecinct);
    const save = useFormSave(projectPrecinctMetadata);
    const { data: lookups } = useLookups(projectPrecinctMetadata);
    const projectId = Number(useParams().projectId);
    const {
        data: projectPrecincts,
        loading: projectPrecinctsLoading,
        refresh: precinctsRefresh,
    } = useProjectPrecinctsByProjectId(projectId);
    const { data: project } = useContext(ProjectContext);

    const submit = useCallback(
        async (draft: ProjectPrecinct, _original: ProjectPrecinct) => {
            try {
                const persisted = (await save(draft)) as ProjectPrecinct | undefined;

                await precinctsRefresh();

                return persisted ?? draft;
            } catch (reason) {
                for (const message of errorMessages(reason))
                    snackbar.enqueueSnackbar(message, {
                        variant: "error-critical",
                    });
                throw reason;
            }
        },
        [save, precinctsRefresh, snackbar],
    );

    const addNewPrecinct = useCallback(async () => {
        const draft = new ProjectPrecinct({
            projectId,
        });
        const persisted = (await save(draft)) as ProjectPrecinct | undefined;

        await precinctsRefresh();

        return persisted ?? draft;
    }, [projectId, precinctsRefresh, save]);

    const exportTitle = `${project?.name} - Precincts`;
    const columns = useMemo(() => buildProjectPrecinctColumns(lookups), [lookups]);

    return (
        <>
            <Outlet />
            <TitleBox display="flex" justifyContent="flex-end">
                <StyledButton variant="contained" onClick={addNewPrecinct}>
                    Add Precinct
                </StyledButton>
            </TitleBox>
            <StyledPaper>
                <CollapsibleAppTable
                    density="compact"
                    startCollapsed={false}
                    title="Project Precincts"
                    loading={projectPrecinctsLoading}
                    columns={columns}
                    rows={projectPrecincts ?? []}
                    getRowId={(precinct) => {
                        if (precinct.id == null) {
                            throw new Error("Project precinct is missing its domain ID");
                        }

                        return precinct.id;
                    }}
                    columnControls={{
                        menu: false,
                    }}
                    filterControls
                    groupingEnabled={false}
                    editing={{
                        mode: "row",
                        onCommit: submit,
                    }}
                    export={{
                        fileName: exportTitle,
                        exportTitle,
                        includeDateTime: true,
                    }}
                />
            </StyledPaper>
        </>
    );
};

export default BulkEditProjectPrecincts;
